import Head from 'next/head'
import { FivePound } from 'features/ECommerce/screens/ProductDetail/FivePound'
import { prefetchProductDetail } from 'features/ECommerce/api/queries/useFetchProductDetail'
import { GetServerSidePropsContext } from 'next'
import { ProductDetail as ProductDetailType } from 'models/Product'
import { useTranslation } from 'hooks/useTranslation'
import { Essentials } from 'features/ECommerce/screens/ProductDetail/Essentials'
import { Startr } from 'features/ECommerce/screens/ProductDetail/Startr'
import { Treats } from 'features/ECommerce/screens/ProductDetail/Treats'
import { prefetchShoppingCart } from 'features/ECommerce/api/queries/useFetchShoppingCart'
import Cookies from 'cookies'
import { CookieKeys } from 'services/Cookies'
import { getPayloadFromJWT } from 'utils/jwt'
import Script from 'next/script'

type ProductDetailWithConstantSlugs = Omit<ProductDetailType, 'slug'> & {
  slug:
    | '5-lb-beef-patty-recipe'
    | '5-lb-chicken-patty-recipe'
    | '5-lb-venison-patty-recipe'
    | '5-lb-duck-patty-recipe'
    | '5-lb-lamb-patty-recipe'
    | '5-lb-turkey-patty-recipe'
    | 'big-box'
    | 'classic-box'
    | 'small-box'
    | 'startr'
    | 'treats'
}

const patties = [
  '5-lb-chicken-patty-recipe',
  '5-lb-beef-patty-recipe',
  '5-lb-venison-patty-recipe',
  '5-lb-duck-patty-recipe',
  '5-lb-lamb-patty-recipe',
  '5-lb-turkey-patty-recipe',
]

const essentials = ['small-box', 'classic-box', 'big-box']

const treats = 'treats'
const startr = 'startr'

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const { productId } = ctx.query as { productId: string }
  if (productId === 'classic-boxg') {
    return {
      redirect: {
        destination: '/products/classic-box',
        permanent: false,
      },
    }
  }

  const cookies = new Cookies(ctx.req, ctx.res)

  const { dehydratedState, data } = await prefetchProductDetail(productId)

  const authToken = cookies.get(CookieKeys.SESSION)
  const guestId = cookies.get(CookieKeys.GUEST_ID)
  const clientId = authToken ? getPayloadFromJWT(authToken).sub : guestId
  const dehydratedCartState = await prefetchShoppingCart(clientId || '')

  if (
    !patties.includes(productId) &&
    !essentials.includes(productId) &&
    startr !== productId &&
    treats !== productId
  ) {
    return { notFound: true }
  }

  if (dehydratedState.queries[0]?.queryKey[1] !== productId && treats !== productId) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...dehydratedState,
      metaData: data || '',
      slug: productId,
      ...dehydratedCartState,
    },
  }
}

export default function ProductDetailPage({
  metaData,
  slug,
}: {
  metaData: ProductDetailWithConstantSlugs
  slug: ProductDetailWithConstantSlugs['slug']
}) {
  const { t } = useTranslation()

  const { pageTitle, title, description } = {
    pageTitle: t(`products:meta.${slug}.page-title`),
    title: t(`products:meta.${slug}.title`),
    description: t(`products:meta.${slug}.description`),
  }

  const canonicalUrl = `https://wefeedraw.com/products/${slug}`

  // prettier-ignore
  const schema = metaData ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${title}`,
    url: `https://wefeedraw.com/products/${metaData.slug}`,
    image: `https://wefeedraw.com${metaData.image}`,
    description: description,
    brand: {
      '@type': 'Brand',
      name: 'We Feed Raw'
    },
    offers: {
      '@type': 'Offer',
      url: `https://wefeedraw.com/products/${metaData.slug}`,
      priceCurrency: 'USD',
      price: `${metaData.price.total/100}`,
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'We Feed Raw'
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0.00',
          currency: 'USD'
        }
      },
      additionalProperty: {
        '@type': 'PropertyValue',
        name: 'Shipping',
        value: 'Free Shipping'
      }
    }
  } : {};

  const jsonLdString = JSON.stringify(schema)

  const ProductDetail = () => {
    if (patties.includes(metaData?.slug)) {
      return <FivePound />
    } else if (essentials.includes(metaData?.slug)) {
      return <Essentials />
    } else if (metaData?.slug === startr) {
      return <Startr />
    } else {
      return <Treats />
    }
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      {metaData && (
        <Script
          id="schema-script"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdString }}
        />
      )}
      <ProductDetail />
    </>
  )
}
